import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, CheckCircle, X } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';
import Button from '@/components/ui/Button';

export default function GiftCardRedemption({ onApply }: { onApply?: (code: string, balance: number) => void }) {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const [code, setCode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<{ balance: number; code: string } | null>(null);
  const [error, setError] = useState('');

  const config = data?.settings?.upsell_config?.gift_cards;
  if (!config?.enabled) return null;

  async function handleCheck(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;

    setIsChecking(true);
    setError('');
    setResult(null);

    try {
      const res = await api.get<{ balance: number; code: string }>(`/gift-cards/check/${code.trim()}`);
      setResult(res);
      onApply?.(res.code, res.balance);
    } catch {
      setError(t('giftCard.invalid'));
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <div className="border border-line rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="h-4 w-4 text-sage-600" aria-hidden="true" />
        <span className="text-sm font-medium text-ink">{t('giftCard.title')}</span>
      </div>

      {result ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-success-600" aria-hidden="true" />
            <span className="text-sm text-success-700">
              {t('giftCard.balance', { code: result.code, amount: `$${result.balance.toFixed(2)}` })}
            </span>
          </div>
          <button
            onClick={() => { setResult(null); setCode(''); }}
            className="text-ink-muted hover:text-ink-soft"
            aria-label={t('giftCard.removeAriaLabel')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <form onSubmit={handleCheck} className="flex gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('giftCard.placeholder')}
            className="flex-1 px-3 py-2 border border-line-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
          />
          <Button type="submit" variant="outline" size="sm" isLoading={isChecking}>
            {t('giftCard.apply')}
          </Button>
        </form>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
